import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppHandlers } from "../../../services/actions/app/useAppHandlers";
import { useAuthRequest } from "../../../services/apis/auth/useAuthRequest";
import Cookies from "js-cookie";
import { useAuthHandlers } from "../../../services/actions/auth/useAuthHandlers";
import { analytic, getPageTitle, getPageUrl } from "../../../utils";
import styled from "styled-components";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { AppRoutes } from "../../../interfaces/store/appStore";
import {
  AuthCard,
  AuthInnerContainer,
  AuthMethodButton,
  AuthOnboardingCardSubtitle,
  AuthOnboardingPageTitle,
  AuthPageContainer,
  FullLogo,
} from "../authFormCommon";
import AuthLegalFooter from "../components/AuthLegalFooter";
import CheckInboxCard from "../components/CheckInboxCard";
import { saveLastLoginUser } from "../lastLoginUserStorage";
import { skipExtensionForSurveyOnboarding } from "../../../pages/onboarding/onboardingExtensionSkipUtils";
import { clearInvitePendingVerification, getInvitePendingVerification, logoutExistingUserForInviteAuth } from "../../../interfaces/models/invite";
import { useAuthStore } from "../../../stores/auth/useAuthStore";

type VerifyEmailPageState = "loading" | "already_verified" | "expired" | "error";

type VerifyEmailApiResponse = {
  success?: boolean;
  message?: string;
  data?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    password?: string;
    inviteCode?: string;
  };
};

const isAlreadyVerifiedMessage = (message?: string) =>
  Boolean(message?.toLowerCase().includes("already verified"));

const getEmailFromVerificationToken = (token: string): string | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { email?: string };

    return decoded.email?.trim() || null;
  } catch {
    return null;
  }
};

const resolveVerificationEmail = (token: string): string => {
  return getEmailFromVerificationToken(token) ?? getInvitePendingVerification()?.email ?? "";
};

const isResendableVerificationFailure = (message?: string, email?: string | null) => {
  if (!email) return false;

  const lower = message?.toLowerCase() ?? "";
  return lower.includes("expired") || lower.includes("invalid or expired") || lower === "invalid token";
};

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const [pageState, setPageState] = React.useState<VerifyEmailPageState>("loading");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [verificationEmail, setVerificationEmail] = React.useState("");
  const [resendLoading, setResendLoading] = React.useState(false);
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useAppHandlers();
  const { verifyEmail, signUpEmail, resendVerificationEmail: resendVerificationEmailRequest } = useAuthRequest();
  const authStore = useAuthStore();
  const authRef = React.useRef(authStore);
  authRef.current = authStore;
  const {
    logoutHandler,
    setAuthDataHandler,
    createAndLoginToWorkspaceHandler,
    saveOnboardingData,
  } = useAuthHandlers();

  const cleanUp = React.useCallback(() => {
    window.history.pushState({}, "", window.location.pathname);
  }, []);

  const verifyStartedRef = React.useRef(false);

  const resendVerificationEmail = React.useCallback(async () => {
    if (!verificationEmail) return;

    setResendLoading(true);
    try {
      const response = await resendVerificationEmailRequest({ email: verificationEmail });

      if (response?.success) {
        showSuccessToast({ message: "Verification email sent! Please check your inbox." });
      } else {
        showErrorToast({ message: response?.message || "Unable to resend verification email." });
      }
    } catch (error) {
      console.error("Resend verification email error:", error);
      showErrorToast({ message: "Unexpected error occurred." });
    } finally {
      setResendLoading(false);
    }
  }, [verificationEmail, resendVerificationEmailRequest, showSuccessToast, showErrorToast]);

  const init = React.useCallback(async () => {
    if (verifyStartedRef.current) return;
    verifyStartedRef.current = true;

    const token = params.get("token");
    if (!token) return;

    const verifyPath = `${window.location.pathname}${window.location.search}`;
    await logoutExistingUserForInviteAuth(
      Boolean(authRef.current.store.user),
      logoutHandler,
      verifyPath,
    );

    try {
      const res = (await verifyEmail(token)) as VerifyEmailApiResponse | null;

      if (res?.success === false && isAlreadyVerifiedMessage(res.message)) {
        cleanUp();
        setPageState("already_verified");
        return;
      }

      const verified = res?.data;
      if (verified?.email) {
        showSuccessToast({
          message: "Email verified successfully! Redirecting...",
        });

        try {
          const response = await signUpEmail(
            {
              email: verified.email,
              firstName: verified.firstName,
              lastName: verified.lastName,
              password: verified.password,
              inviteCode: verified.inviteCode,
            },
            {
              params: {
                fp_uid: Cookies.get("_fprom_ref"),
                fp_tid: Cookies.get("_fprom_tid"),
              },
            }
          );
          if (response?.user) {
            clearInvitePendingVerification();
            saveLastLoginUser(response.user, "email");
            const newAccessToken = `Bearer ${response.token}`;
            const newRefreshToken = response.refreshToken;

            if (!response?.workspace) {
              await createAndLoginToWorkspaceHandler(
                {
                  name: response?.user?.email,
                  isPersonal: true,
                  invites: [],
                },
                {
                  Authorization: newAccessToken,
                  "refresh-token": newRefreshToken,
                }
              );
            } else {
              await setAuthDataHandler({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              });
            }

            await skipExtensionForSurveyOnboarding(saveOnboardingData);

            analytic({
              event: "free_trial_signup",
              pageUrl: getPageUrl(),
              pageTitle: getPageTitle(),
              method: "credentials",
            });

            cleanUp();
            navigate(AppRoutes.PROCESSING, { replace: true });
          }
        } catch (error) {
          console.log("Sign up with email", error);
        }
      } else {
        const message = res?.message || "Verification failed.";
        const email = resolveVerificationEmail(token);

        if (isResendableVerificationFailure(message, email)) {
          cleanUp();
          setVerificationEmail(email);
          setPageState("expired");
          return;
        }

        setErrorMessage(message);
        setPageState("error");
      }
    } catch {
      setErrorMessage("Unexpected error during verification.");
      setPageState("error");
    }
  }, [logoutHandler, navigate, params, showSuccessToast, verifyEmail, signUpEmail, setAuthDataHandler, createAndLoginToWorkspaceHandler, saveOnboardingData, cleanUp]);

  React.useEffect(() => {
    void init();
  }, [init]);

  return (
    <AuthPageContainer>
      <AuthInnerContainer>
        <VerifyCard>
          <FullLogo />
          <Body>
            {pageState === "loading" && (
              <>
                <SpinnerWrap>
                  <LoadingSpinner height="58px" width="58px" />
                </SpinnerWrap>
                <TextBlock>
                  <VerifyTitle>Let&apos;s get you started</VerifyTitle>
                  <AuthOnboardingCardSubtitle>
                    This should only take a few seconds
                  </AuthOnboardingCardSubtitle>
                </TextBlock>
              </>
            )}
            {pageState === "already_verified" && (
              <TextBlock>
                <VerifyTitle>Email already verified</VerifyTitle>
                <AuthOnboardingCardSubtitle>
                  This verification link has already been used. Log in to continue to your account.
                </AuthOnboardingCardSubtitle>
                <AuthMethodButton
                  $type="blue"
                  tooltipMessage=""
                  onClickHandler={() =>
                    navigate(AppRoutes.SIGNIN, {
                      replace: true,
                      state: { fromAlreadyVerified: true },
                    })
                  }
                >
                  Go to log in
                </AuthMethodButton>
              </TextBlock>
            )}
            {pageState === "expired" && (
              <>
                <TextBlock>
                  <VerifyTitle>Verification link expired</VerifyTitle>
                  <ExpiredSubtitle>
                    This verification link is no longer active. Send a new verification link to{" "}
                    {verificationEmail} to continue.
                  </ExpiredSubtitle>
                </TextBlock>
                <CheckInboxCard
                  loading={resendLoading}
                  onResend={resendVerificationEmail}
                  resendPosition="top"
                  resendButtonStyle="outline"
                />
                <AuthMethodButton
                  $type="blue"
                  tooltipMessage=""
                  onClickHandler={() => navigate(AppRoutes.SIGNIN, { replace: true })}
                >
                  Go to log in
                </AuthMethodButton>
              </>
            )}
            {pageState === "error" && (
              <TextBlock>
                <VerifyTitle>Verification failed</VerifyTitle>
                <AuthOnboardingCardSubtitle>{errorMessage}</AuthOnboardingCardSubtitle>
                <AuthMethodButton
                  $type="blue"
                  tooltipMessage=""
                  onClickHandler={() => navigate(AppRoutes.SIGNIN, { replace: true })}
                >
                  Go to log in
                </AuthMethodButton>
              </TextBlock>
            )}
          </Body>
        </VerifyCard>
      </AuthInnerContainer>
      <AuthLegalFooter />
    </AuthPageContainer>
  );
}

const VerifyCard = styled(AuthCard)`
  align-items: center;
  gap: 32px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const VerifyTitle = styled(AuthOnboardingPageTitle)`
  font-size: 24px;
`;

const ExpiredSubtitle = styled(AuthOnboardingCardSubtitle)`
  text-align: center;
`;

const SpinnerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 61px;
`;
